/**
 * Archelia In-House Analytics Engine
 * Raccoglie eventi (PAGE_VIEW, SCROLL_DEPTH, SEARCH, OUT_OF_STOCK_CLICK)
 * e li invia in batch al Middleware via Shopify App Proxy.
 * Parte solo dopo l'evento 'archelia:tracking:ready' (gate GDPR).
 */

(function () {
  'use strict';

  const ENDPOINT = '/apps/archelia/api/webhooks/shopify/analytics_spy';
  const BATCH_INTERVAL_MS = 10000;
  const SCROLL_THRESHOLDS = [25, 50, 75, 90];

  let analyticsQueue = [];
  let pageEnteredAt = performance.now();
  let maxScrollDepth = 0;
  let scrollThresholdsHit = new Set();
  let dispatchTimer = null;
  let initialized = false;

  // ─── Dispatcher con Retry ───────────────────────────────────────────────────
  async function dispatchAnalytics() {
    if (analyticsQueue.length === 0) return;

    const snapshot = [...analyticsQueue];
    const T = window.ArcheliaTracking;

    const payload = {
      deviceId: T.deviceId,
      sessionId: T.sessionId,
      customerEmail: T.customerEmail || null,
      sessionData: T.sessionParams,
      events: snapshot
    };

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify(payload)
      });
      // Svuota la coda SOLO su HTTP 200 — altrimenti retry al prossimo interval
      if (res.ok) {
        analyticsQueue = [];
      }
    } catch (e) {
      // Errore di rete: coda non svuotata, retry automatico
    }
  }

  // ─── PAGE VIEW ──────────────────────────────────────────────────────────────
  function pushPageView() {
    const viewDurationMs = Math.round(performance.now() - pageEnteredAt);
    analyticsQueue.push({
      type: 'PAGE_VIEW',
      path: window.location.pathname,
      viewDurationMs: viewDurationMs,
      maxScrollDepth: maxScrollDepth,
      timestamp: Date.now()
    });
  }

  // ─── SCROLL DEPTH ───────────────────────────────────────────────────────────
  let scrollThrottle = false;
  function onScroll() {
    if (scrollThrottle) return;
    scrollThrottle = true;
    setTimeout(function () {
      scrollThrottle = false;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((window.scrollY / docHeight) * 100);
      if (pct > maxScrollDepth) maxScrollDepth = pct;

      SCROLL_THRESHOLDS.forEach(function (threshold) {
        if (pct >= threshold && !scrollThresholdsHit.has(threshold)) {
          scrollThresholdsHit.add(threshold);
          analyticsQueue.push({
            type: 'SCROLL_DEPTH',
            path: window.location.pathname,
            depth: threshold,
            timestamp: Date.now()
          });
        }
      });
    }, 200);
  }

  // ─── SEARCH ─────────────────────────────────────────────────────────────────
  // Traccia al submit del form di ricerca (cattura il termine) +
  // al caricamento della pagina risultati (cattura resultsCount)
  let searchTrackedForThisPage = false;

  function pushSearchEvent(term, resultsCount) {
    if (!term) return;
    analyticsQueue.push({
      type: 'SEARCH',
      payload: {
        term: term,
        resultsCount: resultsCount !== undefined ? resultsCount : null
      },
      timestamp: Date.now()
    });
  }

  function initSearchTracking() {
    // Traccia SOLO sulla pagina risultati per evitare il double-firing
    // (il listener sul submit sparava l'evento anche PRIMA del caricamento risultati)
    if (!window.location.pathname.startsWith('/search')) return;
    if (searchTrackedForThisPage) return;
    searchTrackedForThisPage = true;

    // Usa i dati Liquid iniettati nel <head> (accurati al 100%)
    if (window._archeliaSearchData && window._archeliaSearchData.terms) {
      pushSearchEvent(
        window._archeliaSearchData.terms,
        window._archeliaSearchData.resultsCount
      );
      return;
    }

    // Fallback: leggi da URL params + DOM (se Liquid non disponibile)
    const params = new URLSearchParams(window.location.search);
    const term = params.get('q') || params.get('query') || '';
    if (!term) return;

    const resultsEl = document.querySelector('[data-results-count]');
    let resultsCount = 0;
    if (resultsEl) {
      resultsCount = parseInt(resultsEl.getAttribute('data-results-count'), 10) || 0;
    } else {
      const cards = document.querySelectorAll('.product-card, .card--product, [data-product-id]');
      resultsCount = cards.length;
    }
    pushSearchEvent(term, resultsCount);
  }

  // ─── OUT OF STOCK CLICK (pagine prodotto) ───────────────────────────────────
  function initOutOfStockTracking() {
    // Deleghiamo su document per compatibilità con varianti caricate dinamicamente
    document.addEventListener('click', function (e) {
      const target = e.target.closest('input[type="radio"], button, [data-option-value]');
      if (!target) return;

      const isDisabled =
        target.disabled ||
        target.getAttribute('aria-disabled') === 'true' ||
        target.classList.contains('disabled') ||
        target.classList.contains('soldout') ||
        target.classList.contains('unavailable');

      if (!isDisabled) return;

      // Estrai productId e variantId dal DOM del tema
      const productForm = target.closest('form[action="/cart/add"]') ||
                         target.closest('[data-product-id]') ||
                         target.closest('.product');
      const productId = productForm
        ? (productForm.getAttribute('data-product-id') || productForm.querySelector('[name="id"]')?.value || null)
        : null;
      const variantId = target.value || target.getAttribute('data-value') || null;

      // Prezzo attuale visualizzato (già scontato)
      const priceEl = document.querySelector('.price--sale .price-item--sale, .price-item--regular, [data-product-price]');
      let price = null;
      if (priceEl) {
        const raw = priceEl.textContent.replace(/[^\d,\.]/g, '').replace(',', '.');
        price = parseFloat(raw) || null;
      }

      analyticsQueue.push({
        type: 'OUT_OF_STOCK_CLICK',
        productId: productId ? String(productId) : null,
        variantId: variantId ? String(variantId) : null,
        price: price,
        timestamp: Date.now()
      });
    }, { passive: true });
  }

  // ─── Init principale ─────────────────────────────────────────────────────────
  function init() {
    if (initialized) return;
    initialized = true;

    // Registra la page view con scroll tracking
    window.addEventListener('scroll', onScroll, { passive: true });

    // Invia page view + flush alla chiusura/cambio tab
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        pushPageView();
        dispatchAnalytics();
      }
    });
    window.addEventListener('pagehide', function () {
      pushPageView();
      dispatchAnalytics();
    });

    // Batch periodico ogni 10 secondi
    dispatchTimer = setInterval(dispatchAnalytics, BATCH_INTERVAL_MS);

    // Hook Search: submit form + pagina risultati
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSearchTracking);
    } else {
      initSearchTracking();
    }

    // Hook Out of Stock
    initOutOfStockTracking();
  }

  // ─── Attendi il gate GDPR ────────────────────────────────────────────────────
  if (window.ArcheliaTracking) {
    init();
  } else {
    window.addEventListener('archelia:tracking:ready', init, { once: true });
  }
})();
